import { buildPublicJsonHeaders } from "./publicCatalogueHelpers";

export type CatalogueCommerceContext = {
  catalogueId: string;
  /** Path param from `/catalogue-product-info/:id` (usually productUniqueId) when API omits Mongo `_id`. */
  routeProductId?: string;
  shareToken?: string;
  linkToken?: string;
  sessionToken?: string;
  priceTier?: string;
  guestName?: string;
  guestPhone?: string;
  /** Reseller sharing: a reseller's client sees no cart and never the studio. */
  audience?: "studio" | "direct" | "reseller" | "client";
  curatorName?: string | null;
  curatorWhatsApp?: string | null;
  /** Direct buyer / reseller: message the studio about this piece. */
  studioWhatsApp?: string | null;
  liked?: boolean;
  onToggleLike?: () => void;
  /** The studio allows downloads: photos can be saved, as a stamped copy. */
  allowDownload?: boolean;
};

const ANON_CLIENT_LABEL = "Catalogue visitor";

/**
 * CRA env files set REACT_APP_SERVERURL to host + `/api/v1` (no trailing slash).
 * Compose paths relative to that root — do not prefix `/api/v1` again.
 */
export function resolveApiV1Root(): string {
  const raw = (process.env.REACT_APP_SERVERURL || "").trim();
  if (!raw) return "";
  let base = raw.replace(/\/+$/, "");
  if (!base.endsWith("/api/v1")) {
    base = `${base}/api/v1`;
  }
  return base;
}

type ApiEnvelope = {
  success?: boolean;
  message?: string;
  data?: unknown;
  status?: number;
};

async function postJson(path: string, body: unknown): Promise<ApiEnvelope> {
  const root = resolveApiV1Root();
  if (!root) {
    return {
      success: false,
      message: "API URL is not configured (REACT_APP_SERVERURL).",
    };
  }
  const url = `${root}${path.startsWith("/") ? path : `/${path}`}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: buildPublicJsonHeaders() as HeadersInit,
      body: JSON.stringify(body),
    });
  } catch {
    return {
      success: false,
      message:
        "Could not reach the server. Confirm the backend is running and REACT_APP_SERVERURL matches its port.",
    };
  }

  const text = await res.text();
  let parsed: ApiEnvelope | null = null;
  try {
    parsed = text ? (JSON.parse(text) as ApiEnvelope) : null;
  } catch {
    return {
      success: false,
      message: res.ok
        ? "Unexpected response from server."
        : `Request failed (HTTP ${res.status}).`,
    };
  }

  if (parsed && typeof parsed === "object") {
    if (parsed.success === false && !parsed.message) {
      parsed.message = `Request failed (HTTP ${res.status}).`;
    }
    return parsed;
  }

  return {
    success: res.ok,
    message: res.ok ? undefined : `Request failed (HTTP ${res.status}).`,
  };
}

export type WishlistItemPayload = {
  productId: string;
  productName: string;
  productColor?: string;
  productImage?: string;
  designNum?: string;
  notes?: string;
  productUniqueId?: string;
  srNo?: string;
};

export function submitCatalogueShortlist(params: {
  catalogueId: string;
  items: WishlistItemPayload[];
  source?: string;
  clientMessage?: string;
  shareLinkToken?: string;
  clientGst?: string;
  clientAddress?: string;
}) {
  return postJson("/catalogue-wishlist/submit", {
    catalogueId: params.catalogueId,
    clientName: ANON_CLIENT_LABEL,
    clientContact: "",
    clientEmail: "",
    clientMessage: params.clientMessage ?? "",
    source: params.source || "CATALOGUE_PRODUCT_DETAIL",
    shareLinkToken: params.shareLinkToken,
    clientGst: params.clientGst,
    clientAddress: params.clientAddress,
    wishlistItems: params.items,
  });
}

export type CartLinePayload = {
  productId: string;
  productName: string;
  productColor?: string;
  productImage?: string;
  designNum?: string;
  productUniqueId?: string;
  srNo?: string;
  quantity: number;
  sellPriceMrpSnapshot?: string;
  notes?: string;
};

export function submitCatalogueCartIntent(params: {
  catalogueId: string;
  lines: CartLinePayload[];
  source?: string;
  clientMessage?: string;
  shareLinkToken?: string;
  clientGst?: string;
  clientAddress?: string;
}) {
  return postJson("/catalogue-cart/submit", {
    catalogueId: params.catalogueId,
    clientName: ANON_CLIENT_LABEL,
    clientContact: "",
    clientEmail: "",
    clientMessage: params.clientMessage ?? "",
    source: params.source || "CATALOGUE_PRODUCT_DETAIL",
    shareLinkToken: params.shareLinkToken,
    clientGst: params.clientGst,
    clientAddress: params.clientAddress,
    cartItems: params.lines,
  });
}

export function requestCatalogueAccessExtension(params: {
  catalogueId: string;
  clientName?: string;
  clientContact?: string;
  clientEmail?: string;
  clientMessage?: string;
  linkToken?: string;
}) {
  return postJson("/catalogue-inquiry/send", {
    catalogueId: params.catalogueId,
    clientName: params.clientName || ANON_CLIENT_LABEL,
    clientContact: params.clientContact || "",
    clientEmail: params.clientEmail || "",
    clientMessage:
      params.clientMessage ||
      `Access extension requested for shared catalogue${params.linkToken ? ` (link: ${params.linkToken})` : ""}.`,
    shareLinkToken: params.linkToken,
    productIds: [],
    source: "CATALOGUE_EXTENSION_REQUEST",
  });
}

/** Mirrors visitor shelf documents for batch submit from the account page. */
export type ShelfCartLineInput = {
  productKey?: string;
  productId?: string;
  productUniqueId?: string;
  productName?: string;
  productColor?: string;
  productImage?: string;
  designNum?: string;
  quantity?: number;
  sellPriceMrpSnapshot?: string;
  shareLinkToken?: string;
};

export type ShelfShortlistLineInput = {
  productKey?: string;
  productId?: string;
  productUniqueId?: string;
  productName?: string;
  productColor?: string;
  productImage?: string;
  designNum?: string;
  shareLinkToken?: string;
};

export type VisitorShelfSubmitInput = {
  catalogueId: string;
  cartLines?: ShelfCartLineInput[];
  shortlistLines?: ShelfShortlistLineInput[];
};

const SOURCE_VISITOR_ACCOUNT = "CATALOGUE_VISITOR_ACCOUNT";

function clampQtyAccount(n: number): number {
  return Math.min(999, Math.max(1, Math.trunc(n)));
}

function mapShelfCartLine(line: ShelfCartLineInput): CartLinePayload {
  const pk = String(line.productKey || line.productId || "").trim();
  const id = String(line.productId || pk || line.productUniqueId || "").trim();
  return {
    productId: id || pk,
    productName: line.productName || "Product",
    productColor: line.productColor,
    productImage: line.productImage,
    designNum: line.designNum,
    productUniqueId: line.productUniqueId,
    quantity: clampQtyAccount(Number(line.quantity) || 1),
    sellPriceMrpSnapshot: line.sellPriceMrpSnapshot,
    // Preserve source link for attribution.
    ...(line.shareLinkToken ? { shareLinkToken: line.shareLinkToken } : {}),
  };
}

function mapShelfShortlistLine(line: ShelfShortlistLineInput): WishlistItemPayload {
  const pk = String(line.productKey || line.productId || "").trim();
  const id = String(line.productId || pk || line.productUniqueId || "").trim();
  return {
    productId: id || pk,
    productName: line.productName || "Product",
    productColor: line.productColor,
    productImage: line.productImage,
    designNum: line.designNum,
    productUniqueId: line.productUniqueId,
    ...(line.shareLinkToken ? { shareLinkToken: line.shareLinkToken } : {}),
  };
}

/** What to send from the shelf: cart only, shortlist only, or both (default). */
export type VisitorSubmitMode = "cart" | "shortlist" | "both";

/**
 * Sends one shelf (cart and/or shortlist for a catalogue) to the organisation.
 * Visitor JWT + headers supply the client’s name, phone, and email on the backend.
 */
export async function submitVisitorShelfToOrganisation(
  shelf: VisitorShelfSubmitInput,
  opts?: {
    clientMessage?: string;
    mode?: VisitorSubmitMode;
    clientGst?: string;
    clientAddress?: string;
  }
): Promise<{ ok: boolean; message?: string }> {
  const cid = String(shelf.catalogueId || "").trim();
  if (!cid) {
    return { ok: false, message: "Missing catalogue." };
  }
  const mode = opts?.mode ?? "both";
  const cartRaw = Array.isArray(shelf.cartLines) ? shelf.cartLines : [];
  const slRaw = Array.isArray(shelf.shortlistLines) ? shelf.shortlistLines : [];

  const submitCart =
    (mode === "both" || mode === "cart") && cartRaw.length > 0;
  const submitShortlist =
    (mode === "both" || mode === "shortlist") && slRaw.length > 0;

  if (!submitCart && !submitShortlist) {
    if (mode === "cart") {
      return { ok: false, message: "Your cart is empty." };
    }
    if (mode === "shortlist") {
      return { ok: false, message: "Your shortlist is empty." };
    }
    return { ok: false, message: "Nothing to send for this collection." };
  }

  const note =
    opts?.clientMessage ??
    (mode === "cart"
      ? "Cart submitted from my visitor account."
      : mode === "shortlist"
        ? "Shortlist submitted from my visitor account."
        : "Final selection submitted from my visitor account (cart and shortlist as shown).");

  if (submitCart) {
    const lines = cartRaw.map(mapShelfCartLine);
    const shareLinkToken =
      String((cartRaw[0] as any)?.shareLinkToken || (slRaw[0] as any)?.shareLinkToken || "").trim() || undefined;
    const j = await submitCatalogueCartIntent({
      catalogueId: cid,
      lines,
      source: SOURCE_VISITOR_ACCOUNT,
      clientMessage: note,
      shareLinkToken,
      clientGst: opts?.clientGst,
      clientAddress: opts?.clientAddress,
    });
    if (!j.success) {
      return { ok: false, message: j.message || "Could not send cart to the studio." };
    }
  }

  if (submitShortlist) {
    const items = slRaw.map(mapShelfShortlistLine);
    const shareLinkToken =
      String((slRaw[0] as any)?.shareLinkToken || (cartRaw[0] as any)?.shareLinkToken || "").trim() || undefined;
    const j = await submitCatalogueShortlist({
      catalogueId: cid,
      items,
      source: SOURCE_VISITOR_ACCOUNT,
      clientMessage: note,
      shareLinkToken,
      clientGst: opts?.clientGst,
      clientAddress: opts?.clientAddress,
    });
    if (!j.success) {
      return { ok: false, message: j.message || "Could not send shortlist to the studio." };
    }
  }

  return { ok: true };
}

/** Sends every non-empty shelf to the organisation (one cart + one shortlist request per catalogue as needed). */
export async function submitAllVisitorShelvesToOrganisation(
  shelves: VisitorShelfSubmitInput[],
  opts?: { clientMessage?: string }
): Promise<{ ok: boolean; message?: string }> {
  for (const s of shelves) {
    const cartN = s.cartLines?.length ?? 0;
    const slN = s.shortlistLines?.length ?? 0;
    if (cartN === 0 && slN === 0) continue;
    const r = await submitVisitorShelfToOrganisation(s, opts);
    if (!r.ok) return r;
  }
  return { ok: true };
}
